import { Box, Button, Grid, Typography } from "@mui/material";
import { get, set } from "lodash";
import { set as fpSet } from "lodash/fp";
import { FC, useContext, useEffect, useState } from "react";
import useAsyncEffect from "use-async-effect";
import { HistoryProps } from "../..";
import { medicalsApi } from "../../../../../../api/medicalsApi";
import ComponentLoader from "../../../../../../components/ComponentLoader";
import {
  NotificationVariant,
  NotificationsContext,
} from "../../../../../../components/dashboard/NotificationsProvider";
import NotesInput from "../../../../../../components/form/NotesInput";
import useComponentLoader from "../../../../../../hooks/useComponentLoader";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import { useSelector } from "../../../../../../store";
import {
  createEncounterParam,
  createReference,
} from "../../../../../../utils/DataTransformationUtils";
import { FHIRURL } from "../../../../../../utils/FHIRURL";
import {
  QuestionnaireItem,
  QuestionnaireItemTypes,
  createFlattendResponses,
  createQuestionnaireItemPath,
  createResponseItem,
  enableQuestion,
  unpackGroupResponse,
} from "../../../components/QuestionnaireItemTypes";
import {
  SOCIAL_HISTORY_QUESTIONNAIRE_CODE,
  emptyHistoryItems,
} from "../HistoryHelpers";
import MemoizedSocialQuestionnaire from "./SocialQuestionnaireGroup";
import { useDictationCheck } from "../../../../../../hooks/useDictationCheck";

const SocialHistoryForm: FC<HistoryProps> = ({
  item,
  patientId,
  patientReference,
  disabled,
  setSaved,
}) => {
  const { isDictationActive } = useDictationCheck();

  const [loading, toggleLoader] = useComponentLoader();
  const practitionerReference = useSelector(
    (state) => state.user.practitionerReference,
  );
  const { setNotification } = useContext(NotificationsContext);
  const {
    encounterReference,
    setEncounterToInProgress,
    refreshPatientHistory,
  } = useMedicalEncounter();
  const [questionnaire, setQuestionnaire] = useState<any>();
  const [socialHistoryResponse, setSocialHistoryResponse] = useState<any>({
    ...emptyHistoryItems.social(patientReference),
  });
  const [flattenedAnswers, setFlattenedAnswers] = useState({});
  const [notes, setNotes] = useState<Record<string, any>>({});

  useAsyncEffect(async () => {
    try {
      toggleLoader(true);
      const _response = await medicalsApi.findQuestionnaireResponse(
        SOCIAL_HISTORY_QUESTIONNAIRE_CODE,
        {
          subject: patientReference.identifier,
          e: encounterReference?.identifier,
          "exclude-status": "entered-in-error",
        },
      );

      if (!Array.isArray(_response) || _response.length > 1) {
        throw new Error(
          "API response for social history not in correct format",
        );
      }

      let response;
      if (_response.length === 1 && socialHistoryResponse) {
        response = _response[0];
        setSocialHistoryResponse(response);
      }

      let _questionnaire: any;
      if (response?.questionnaire) {
        const fhirUrl = new FHIRURL(response.questionnaire);
        _questionnaire = await medicalsApi.findEntities(
          {
            id: fhirUrl.id,
            version: fhirUrl.searchParams.get("version"),
            code: fhirUrl.searchParams.get("code"),
          },
          "questionnaire",
        );
      } else {
        _questionnaire = await medicalsApi.findEntities(
          { code: SOCIAL_HISTORY_QUESTIONNAIRE_CODE, status: "active" },
          "questionnaire",
        );
      }

      if (!_questionnaire || _questionnaire.length === 0) {
        throw new Error(
          "Social history questionnaire is either more than or less than 1",
        );
      }

      setQuestionnaire(_questionnaire[0]);
    } catch (e) {
      console.error(e);
      setNotification({
        message:
          "Unable to retrieve social history for the patient. Please try again later.",
        variant: NotificationVariant.error,
      });
    } finally {
      toggleLoader(false);
    }
  }, []);

  useEffect(() => {
    if (socialHistoryResponse) {
      const flattened = createFlattendResponses(
        socialHistoryResponse.item || [],
      );
      const [answers, notes] = unpackGroupResponse(socialHistoryResponse || []);
      setNotes(notes);
      setFlattenedAnswers(flattened);
    }
  }, [socialHistoryResponse]);

  const updateItem = (field, value) => {
    const socialHistory = { ...socialHistoryResponse };
    set(socialHistory, field, value);
    setSocialHistoryResponse(socialHistory);
    setSaved(false);
  };

  const saveSocialHistory = async (e) => {
    e.preventDefault();
    toggleLoader(true);
    try {
      if (!patientReference?.reference) {
        console.error(
          "Unable to save social history. Patient information is missing",
        );
        throw new Error(
          "Unable to save social history. Patient information is missing",
        );
      }

      // TODO: Removed the encounter reference from the one time questionnaire.
      // Delete existing social/sexual history questionnaires and create a new
      // ones to test this.
      const socialHistory: any = {
        ...socialHistoryResponse,
        questionnaire: new FHIRURL(`Questionnaire/${questionnaire.id}`, {
          version: questionnaire.version,
          code: questionnaire.code[0]?.code,
        }).url,
        authored: new Date(),
        author: practitionerReference,
        source: patientReference,
      };

      if (encounterReference) {
        socialHistory.encounter = encounterReference;
      }

      if (socialHistory.id) {
        const updatedResponse = await medicalsApi.updateEntity(
          socialHistory.id,
          { ...socialHistory, status: "amended" },
          "questionnaire-response",
          createEncounterParam(encounterReference),
        );
        setSocialHistoryResponse(updatedResponse);
      } else {
        const savedResponse = await medicalsApi.createEntity(
          {
            ...socialHistory,
            status: "completed",
            subject: patientReference ?? createReference("Patient", patientId),
          },
          "questionnaire-response",
          createEncounterParam(encounterReference),
        );
        setSocialHistoryResponse(savedResponse);
      }

      // can be changed to await
      setEncounterToInProgress();

      setSaved(true);
      refreshPatientHistory();
      setNotification({
        variant: NotificationVariant.success,
        message: "Successfully saved the patient's social history.",
      });
    } catch (e) {
      console.error(
        `An error occurred while saving the patient's social history. Please try again.${e}`,
      );
      setNotification({
        variant: NotificationVariant.error,
        message:
          "An error occurred while saving patient's social history. Please try again.",
      });
    } finally {
      toggleLoader(false);
    }
  };

  const onItemChange = (
    item: QuestionnaireItem,
    value,
    allowProgrammatic?: boolean,
  ) => {
    if (item?.linkId !== undefined) {
      const indices = item.linkId.split(".");
      const path: any = createQuestionnaireItemPath(indices);
      const socialHistory = { ...socialHistoryResponse };
      set(socialHistory, path, createResponseItem(item, value));
      setSocialHistoryResponse(socialHistory);
    }

    if (!allowProgrammatic) setSaved(false);
  };
  const onNotesChange = (item: QuestionnaireItem, value) => {
    setSocialHistoryResponse((prev) => {
      let tempResponse = structuredClone(prev);

      const indices = item.linkId.split(".");
      const path = createQuestionnaireItemPath(indices);
      const rootPath = path.split(".")[0];
      const itemAtPath = get(tempResponse, rootPath);

      if (!itemAtPath || itemAtPath.length === 0 || !itemAtPath.linkId) {
        tempResponse = fpSet(
          rootPath,
          {
            linkId: indices[0],
            item: itemAtPath?.item || undefined,
            note: undefined,
          },
          prev,
        );
      }

      tempResponse = fpSet(
        `${rootPath}.text`,
        Array.isArray(questionnaire?.item)
          ? questionnaire?.item?.find((e) => e?.linkId === indices[0])?.text
          : undefined,
        tempResponse,
      );

      tempResponse = fpSet(`${rootPath}.note`, value, tempResponse);

      const [, notes] = unpackGroupResponse(tempResponse);

      setNotes(notes);
      return tempResponse;
    });
    setSaved(false);
  };

  return (
    <form onSubmit={saveSocialHistory}>
      <Box position="relative">
        {questionnaire && (
          <Grid container spacing={3} xs={12} px={1}>
            {questionnaire?.item?.map((item, i) => (
              <>
                {enableQuestion(item, flattenedAnswers, onItemChange) && (
                  <Grid item xs={12} key={`question-${i}`}>
                    <MemoizedSocialQuestionnaire
                      item={item}
                      onItemChange={onItemChange}
                      answers={flattenedAnswers}
                      isDisabled={disabled}
                      enableGroupNote
                      notes={notes}
                      onNotesChange={onNotesChange}
                    />
                  </Grid>
                )}
              </>
            ))}
            <Grid item xs={12}>
              <NotesInput
                disabled={disabled}
                value={socialHistoryResponse?.note}
                label="Comments/Notes"
                onChange={(notes) => updateItem("note", notes)}
                placeholderText="Enter comments/notes about the patient's social history"
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                  disabled={disabled || loading || isDictationActive}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
        {!questionnaire && (
          <Grid>
            <Typography variant="subtitle2">
              Loading social history questionnaire..
            </Typography>
          </Grid>
        )}
        <ComponentLoader loading={loading} />
      </Box>
    </form>
  );
};

export default SocialHistoryForm;
